import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, Circle, CornerDownLeft } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";
import { taskTiming } from "../lib/taskTiming";

const Step = ({ t, focus }) => {
  const [lbl, cls] = taskTiming(t);
  const done = t.status === "done";
  return (
    <div style={{ marginInlineStart: `${Math.min(t.depth || 0, 6) * 18}px` }} data-testid={`chain-step-${t.id}`}
      className={`rounded-xl border p-2.5 ${focus ? "border-blue-300 bg-blue-50/40" : "border-slate-200 bg-white"}`}>
      <div className="flex items-start gap-2">
        {t.depth ? <CornerDownLeft className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" /> : null}
        {done ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : <Circle className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />}
        <div className="min-w-0 flex-1 text-xs">
          <p className="font-bold text-slate-800">{t.created_by_name || "—"} ← {(t.assignees || []).map((a) => a.name).join("، ")}</p>
          <p className="truncate text-slate-500">{t.title}</p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${cls}`} data-testid={`chain-timing-${t.id}`}>{lbl}</span>
            {t.due_date && <span className="text-[10px] text-slate-400">الموعد {t.due_date}</span>}
            {done && <span className="text-[10px] text-emerald-700">أُنجزت {String(t.completed_at || "").slice(0, 10)}{t.completed_by_name ? ` — ${t.completed_by_name}` : ""}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

const order = (items) => {
  const kids = {};
  items.forEach((t) => { (kids[t.parent_id || "_"] = kids[t.parent_id || "_"] || []).push(t); });
  const out = [];
  const walk = (pid) => (kids[pid] || []).forEach((t) => { out.push(t); walk(t.id); });
  walk("_");
  const seen = new Set(out.map((t) => t.id));
  return [...out, ...items.filter((t) => !seen.has(t.id))];
};

export const TaskChainDialog = ({ task, mode = "admin", onClose }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!task) return;
    setItems(null);
    (mode === "portal" ? api.portalTaskChain : api.taskChain)(task.id).then((r) => setItems(order(r.items || []))).catch(() => setItems([]));
  }, [task, mode]);
  return (
    <Dialog open={!!task} onOpenChange={(v) => !v && onClose()}>
      <DialogContent dir="rtl" className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="task-chain-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading">تسلسل المهمة</DialogTitle>
          <DialogDescription>من أرسل المهمة لمن، وحالة كل خطوة وتاريخ إنجازها</DialogDescription>
        </DialogHeader>
        {!items ? <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          : <div className="space-y-2">{items.map((t) => <Step key={t.id} t={t} focus={t.id === task?.id} />)}</div>}
      </DialogContent>
    </Dialog>
  );
};
